// client/src/pages/Register.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isTokenValid } from '../utils/auth';
import { getApiConfig } from '../config/markets.js';

const API_URL = getApiConfig().apiUrl;

export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Estados para los campos del formulario
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 1) Si existiera un token inválido, lo eliminamos al montar
  useEffect(() => {
    if (localStorage.getItem('token') && !isTokenValid()) {
      localStorage.removeItem('token');
    }
    // Si token es válido, React Router (en App.js) redirigirá automáticamente
  }, []);

  // Handler del envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validaciones básicas
    if (!userName || !email || !password || !confirmPassword) {
      setError(t('auth.register.error.required'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.register.error.passwordMatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.register.error.passwordLength'));
      return;
    }

    try {
      const payload = {
        name: userName,
        email: email.trim(),
        password,
        confirmPassword
      };
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        // data.message contiene el texto de error
        setError(data.message || t('auth.register.error.general'));
        return;
      }

      // Si res.ok, data contiene { user: { ... }, message: 'Usuario creado...' }
      setSuccessMessage(data.message || t('auth.register.success'));
      // Por ejemplo, después de 2 segundos redirigir a /login
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Error en /api/register:', err);
      setError(t('auth.login.error.connection'));
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>{t('auth.register.title')}</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.field}>
          <label htmlFor="name" style={styles.label}>{t('auth.register.name')}:</label>
          <input
            id="name"
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            required
            style={styles.input}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="email" style={styles.label}>{t('auth.register.email')}:</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="password" style={styles.label}>{t('auth.register.password')}:</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={styles.input}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="confirmPassword" style={styles.label}>{t('auth.register.confirmPassword')}:</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={styles.input}
          />
        </div>
        {error && <p style={styles.error}>{error}</p>}
        {successMessage && <p style={styles.success}>{successMessage}</p>}
        <button type="submit" style={styles.button}>{t('auth.register.button')}</button>
      </form>
    </div>
  );
}

// Estilos inline órdenes minimalistas
const styles = {
  container: {
    maxWidth: '400px',
    margin: '4rem auto',
    padding: '2rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    backgroundColor: '#fff'
  },
  title: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    fontSize: '1.5rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  field: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc'
  },
  button: {
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#423ef4',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  error: {
    color: 'red',
    marginBottom: '1rem',
    textAlign: 'center'
  },
  success: {
    color: 'green',
    marginBottom: '1rem',
    textAlign: 'center'
  }
};
